import React, { useState, useRef, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  Columns, 
  Download, 
  FileText, 
  FileSpreadsheet, 
  Refresh,
  ChevronRight,
  X,
} from './Icons';
import { Button } from './Button';
import './TableFilters.css';

export interface ColumnConfig {
  key: string;
  label: string;
  defaultVisible?: boolean;
}

export interface FilterOption {
  value: string | number;
  label: string;
}

export interface FilterConfig {
  key: string;
  label: string;
  options: FilterOption[];
  type?: 'select' | 'multiselect';
}

export interface GroupByOption {
  key: string;
  label: string;
}

interface TableFiltersProps {
  // Search
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearch?: () => void;
  
  // Filters
  filters?: FilterConfig[];
  filterValues?: Record<string, string | number | undefined>;
  onFilterChange?: (key: string, value: string | number | undefined) => void;
  
  // Column selection
  columns?: ColumnConfig[];
  visibleColumns?: string[];
  onColumnToggle?: (columns: string[]) => void;
  
  // Export
  onExport?: (format: 'csv' | 'xlsx') => void;
  exportLoading?: boolean;
  
  // Refresh
  onRefresh?: () => void;
  refreshLoading?: boolean;
  
  // Count display
  totalCount?: number;
  shownCount?: number;
  countLabel?: string;  // e.g., "employees", "projects", "clients"

  // Group By (optional)
  groupByOptions?: GroupByOption[];
  activeGroupBy?: string[];
  onGroupByAdd?: (key: string) => void;
  onGroupByRemove?: (key: string) => void;
}

export function TableFilters({
  searchPlaceholder = 'Search...',
  searchValue = '',
  onSearchChange,
  onSearch,
  filters = [],
  filterValues = {},
  onFilterChange,
  columns = [],
  visibleColumns = [],
  onColumnToggle,
  onExport,
  exportLoading = false,
  onRefresh,
  refreshLoading = false,
  totalCount,
  shownCount,
  countLabel = 'items',
  groupByOptions = [],
  activeGroupBy = [],
  onGroupByAdd,
  onGroupByRemove,
}: TableFiltersProps) {
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showGroupByMenu, setShowGroupByMenu] = useState(false);
  const columnMenuRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const groupByMenuRef = useRef<HTMLDivElement>(null);

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target as Node)) {
        setShowColumnMenu(false);
      }
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
      if (groupByMenuRef.current && !groupByMenuRef.current.contains(event.target as Node)) {
        setShowGroupByMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleColumnToggle = (columnKey: string) => {
    if (!onColumnToggle) return;
    const newColumns = visibleColumns.includes(columnKey)
      ? visibleColumns.filter(c => c !== columnKey)
      : [...visibleColumns, columnKey];
    onColumnToggle(newColumns);
  };

  const handleSelectAllColumns = () => {
    if (!onColumnToggle) return;
    onColumnToggle(columns.map(c => c.key));
  };

  const handleDeselectAllColumns = () => {
    if (!onColumnToggle) return;
    // Keep at least one column visible
    const defaultCols = columns.filter(c => c.defaultVisible).map(c => c.key);
    onColumnToggle(defaultCols.length > 0 ? defaultCols : [columns[0].key]);
  };

  const activeFilterCount = Object.values(filterValues).filter(v => v !== undefined && v !== '').length;

  return (
    <div className="table-filters">
      {/* Count display */}
      {totalCount !== undefined && (
        <div className="table-filters-count">
          {shownCount !== undefined && shownCount !== totalCount ? (
            <span>Showing <strong>{shownCount}</strong> of <strong>{totalCount}</strong> {countLabel}</span>
          ) : (
            <span><strong>{totalCount}</strong> {countLabel}</span>
          )}
        </div>
      )}
      
      {/* Search and basic toolbar */}
      <div className="table-filters-row">
        {onSearchChange && (
          <div className="search-box">
            <div className="search-input-wrapper">
              <Search className="search-icon" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onSearch?.()}
                className="search-input"
              />
            </div>
            {onSearch && (
              <Button variant="secondary" size="sm" onClick={onSearch}>
                Search
              </Button>
            )}
          </div>
        )}
        
        <div className="table-filters-actions">
          {/* Filter toggle button */}
          {filters.length > 0 && (
            <Button 
              variant={showFilters ? "primary" : "ghost"} 
              size="sm" 
              onClick={() => setShowFilters(!showFilters)}
              className={activeFilterCount > 0 ? 'has-filters' : ''}
            >
              <Filter />
              Filters
              {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
            </Button>
          )}

          {/* Group By button */}
          {groupByOptions.length > 0 && onGroupByAdd && (
            <div className="dropdown-container" ref={groupByMenuRef}>
              <Button
                variant={activeGroupBy.length > 0 ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setShowGroupByMenu(!showGroupByMenu)}
              >
                ⊞ Group By
                {activeGroupBy.length > 0 && <span className="filter-badge">{activeGroupBy.length}</span>}
              </Button>
              {showGroupByMenu && (
                <div className="dropdown-menu tf-groupby-menu">
                  {groupByOptions.filter(o => !activeGroupBy.includes(o.key)).length === 0 ? (
                    <div className="tf-groupby-empty">All fields selected</div>
                  ) : (
                    groupByOptions
                      .filter(o => !activeGroupBy.includes(o.key))
                      .map(opt => (
                        <button
                          key={opt.key}
                          className="tf-groupby-item"
                          onClick={() => { onGroupByAdd(opt.key); setShowGroupByMenu(false); }}
                        >
                          <ChevronRight className="tf-groupby-icon" />
                          {opt.label}
                        </button>
                      ))
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Column selector */}
          {columns.length > 0 && onColumnToggle && (
            <div className="dropdown-container" ref={columnMenuRef}>
              <Button variant="ghost" size="sm" onClick={() => setShowColumnMenu(!showColumnMenu)}>
                <Columns />
                Columns
              </Button>
              {showColumnMenu && (
                <div className="dropdown-menu column-menu">
                  <div className="dropdown-header">
                    <span>Toggle Columns</span>
                    <div className="dropdown-header-actions">
                      <button onClick={handleSelectAllColumns}>All</button>
                      <button onClick={handleDeselectAllColumns}>Reset</button>
                    </div>
                  </div>
                  <div className="dropdown-body">
                    {columns.map(col => (
                      <label key={col.key} className="column-checkbox">
                        <input
                          type="checkbox"
                          checked={visibleColumns.includes(col.key)}
                          onChange={() => handleColumnToggle(col.key)}
                        />
                        <span>{col.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Export button */}
          {onExport && (
            <div className="dropdown-container" ref={exportMenuRef}>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={exportLoading}
              >
                <Download />
                Export
              </Button>
              {showExportMenu && (
                <div className="dropdown-menu export-menu">
                  <button onClick={() => { onExport('csv'); setShowExportMenu(false); }}>
                    <FileText />
                    Export as CSV
                  </button>
                  <button onClick={() => { onExport('xlsx'); setShowExportMenu(false); }}>
                    <FileSpreadsheet />
                    Export as Excel
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Refresh button */}
          {onRefresh && (
            <Button variant="ghost" size="sm" onClick={onRefresh} disabled={refreshLoading}>
              <Refresh className={refreshLoading ? 'spin' : ''} />
              Refresh
            </Button>
          )}
        </div>
      </div>
      
      {/* Filter dropdowns row */}
      {showFilters && filters.length > 0 && (
        <div className="table-filters-panel">
          {filters.map(filter => (
            <div key={filter.key} className="filter-group">
              <label>{filter.label}</label>
              <select
                value={filterValues[filter.key] ?? ''}
                onChange={(e) => onFilterChange?.(filter.key, e.target.value || undefined)}
              >
                <option value="">All</option>
                {filter.options.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          ))}
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => {
              filters.forEach(f => onFilterChange?.(f.key, undefined));
            }}
          >
            Clear Filters
          </Button>
        </div>
      )}

      {/* Active Group By chips */}
      {activeGroupBy.length > 0 && (
        <div className="tf-active-groups">
          <span className="tf-groups-label">Grouped by:</span>
          {activeGroupBy.map((key) => {
            const opt = groupByOptions.find(o => o.key === key);
            return (
              <span key={key} className="tf-group-chip">
                {opt?.label ?? key}
                {onGroupByRemove && (
                  <button className="tf-chip-remove" onClick={() => onGroupByRemove(key)}>
                    <X />
                  </button>
                )}
              </span>
            );
          })}
          {onGroupByRemove && (
            <button className="tf-clear-groups" onClick={() => activeGroupBy.forEach(k => onGroupByRemove!(k))}>
              Clear All
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default TableFilters;
